import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Sms,
  CheckCircle,
  Error,
  Visibility,
  VisibilityOff,
  Launch,
  ContentCopy
} from '@mui/icons-material';
import api from '../services/api';

interface SMSChannelData {
  name: string;
  provider: string;
  phone_number: string;
  display_name?: string;
  provider_config: Record<string, any>;
  greeting_enabled: boolean;
  greeting_message: string;
  auto_assignment_enabled: boolean;
  auto_response_enabled: boolean;
}

interface Provider {
  name: string;
  description: string;
  features: string[];
  setup_complexity: string;
  documentation_url: string;
  use_cases?: string;
}

interface ConfigField {
  type: string;
  label: string;
  required: boolean;
  default?: any;
  description?: string;
  options?: { value: string; label: string; }[];
}

const steps = [
  'Provider Selection',
  'SMS Configuration',
  'Channel Settings',
  'Verification'
];

interface SMSChannelConfigProps {
  editingChannel?: any;
  onCancel: () => void;
}

const SMSChannelConfig: React.FC<SMSChannelConfigProps> = ({
  editingChannel,
  onCancel
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const [providers, setProviders] = useState<Record<string, Provider>>({});
  const [configFields, setConfigFields] = useState<Record<string, ConfigField>>({});
  const [createdChannel, setCreatedChannel] = useState<any>(null);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  const [formData, setFormData] = useState<SMSChannelData>({
    name: '',
    provider: 'twilio',
    phone_number: '',
    provider_config: {},
    greeting_enabled: true,
    greeting_message: 'Hello! Thank you for texting us. We will respond shortly.',
    auto_assignment_enabled: true,
    auto_response_enabled: false,
  });

  useEffect(() => {
    loadProviders();
    if (editingChannel) {
      loadChannelData();
    }
  }, [editingChannel]);

  useEffect(() => {
    if (formData.provider) {
      loadProviderConfig(formData.provider);
    }
  }, [formData.provider]);

  const loadProviders = async () => {
    try {
      const response = await api.get('/sms/providers');
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to load SMS providers:', error);
      setError('Failed to load providers');
    }
  };

  const loadProviderConfig = async (provider: string) => {
    try {
      const response = await api.get(`/sms/providers/${provider}/config`);
      setConfigFields(response.data.config_fields);
    } catch (error) {
      console.error('Failed to load provider config:', error);
      setError('Failed to load provider configuration');
    }
  };

  const loadChannelData = async () => {
    if (!editingChannel) return;

    try {
      const response = await api.get(`/sms/channels/${editingChannel.id}`);
      const channelData = response.data;

      setFormData({
        name: editingChannel.name,
        provider: channelData.provider || 'twilio',
        phone_number: channelData.phone_number || '',
        provider_config: channelData.provider_config || {},
        greeting_enabled: editingChannel.greeting_enabled,
        greeting_message: editingChannel.greeting_message || 'Hello! Thank you for texting us. We will respond shortly.',
        auto_assignment_enabled: editingChannel.auto_assignment_enabled,
        auto_response_enabled: channelData.auto_response_enabled ?? false,
      });

      setCreatedChannel(channelData);
    } catch (error) {
      console.error('Failed to load channel data:', error);
      setError('Failed to load channel data');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('provider_config.')) {
      const configField = field.replace('provider_config.', '');
      setFormData(prev => ({
        ...prev,
        provider_config: {
          ...prev.provider_config,
          [configField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Provider Selection
        return !!formData.provider;
      case 1: // SMS Configuration
        if (!formData.phone_number) return false;
        return Object.entries(configFields).every(([key, config]) => {
          if (!config.required) return true;
          const value = formData.provider_config[key];
          return value !== undefined && value !== null && value !== '';
        });
      case 2: // Channel Settings
        return !!formData.name;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
      setError(null);
    } else {
      setError('Please fill in all required fields before proceeding.');
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingChannel) {
        setSuccess('SMS channel updated successfully!');
        setTimeout(() => {
          onCancel(); // Close dialog after successful update
        }, 1500);
      } else {
        const response = await api.post('/sms/channels', formData);
        setCreatedChannel(response.data);
        setSuccess('SMS channel created successfully!');
        setActiveStep(3);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || `Failed to ${editingChannel ? 'update' : 'create'} SMS channel`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!createdChannel) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/sms/channels/${createdChannel.id}/verify`);
      setVerificationResult(response.data);
      if (response.data.verified) {
        setSuccess('SMS channel verified successfully!');
      } else {
        setError(response.data.error || 'Verification failed');
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const renderProviderSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select SMS Provider
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(providers).map(([key, provider]) => (
          <Grid item xs={12} md={6} key={key}>
            <Card 
              variant={formData.provider === key ? 'elevation' : 'outlined'}
              sx={{ 
                cursor: 'pointer',
                border: formData.provider === key ? '2px solid #1976d2' : undefined
              }}
              onClick={() => handleInputChange('provider', key)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Sms sx={{ mr: 1 }} />
                  <Typography variant="h6">{provider.name}</Typography>
                </Box>
                <Typography color="text.secondary" paragraph>
                  {provider.description}
                </Typography>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {provider.features.map((feature, index) => (
                      <Chip key={index} label={feature} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Setup: {provider.setup_complexity}
                </Typography>
                {provider.use_cases && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {provider.use_cases}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Launch />}
                  sx={{ mt: 1 }}
                  href={provider.documentation_url}
                  target="_blank"
                >
                  Documentation
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderConfigField = (key: string, config: ConfigField) => {
    const value = formData.provider_config[key] || config.default || '';
    
    if (config.type === 'select') {
      return (
        <FormControl fullWidth key={key}>
          <InputLabel>{config.label}</InputLabel>
          <Select
            value={value}
            label={config.label}
            onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
            required={config.required}
          >
            {config.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    const inputType = config.type === 'password' ? (showPasswords[key] ? 'text' : 'password') : config.type;

    return (
      <TextField
        key={key}
        label={config.label}
        value={value}
        onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
        required={config.required}
        fullWidth
        type={inputType}
        helperText={config.description}
        InputProps={config.type === 'password' ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => togglePasswordVisibility(key)} edge="end">
                {showPasswords[key] ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        } : undefined}
      />
    );
  };

  const renderSMSConfiguration = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure {providers[formData.provider]?.name || 'SMS'} Provider
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          You'll need an active SMS provider account with API credentials to send and receive text messages.
        </Typography>
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Phone Number"
            value={formData.phone_number}
            onChange={(e) => handleInputChange('phone_number', e.target.value)}
            required
            fullWidth
            placeholder="+1234567890"
            helperText="Your SMS phone number (include country code, e.g., +1234567890)"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Display Name"
            value={formData.display_name || ''}
            onChange={(e) => handleInputChange('display_name', e.target.value)}
            fullWidth
            helperText="Friendly name for this phone number (optional)"
          />
        </Grid>

        {Object.entries(configFields).map(([key, config]) => (
          <Grid item xs={12} key={key}>
            {renderConfigField(key, config)}
          </Grid>
        ))}
      </Grid>

      {providers[formData.provider]?.documentation_url && (
        <Box mt={2}>
          <Button
            variant="outlined"
            startIcon={<Launch />}
            href={providers[formData.provider].documentation_url}
            target="_blank"
          >
            SMS Setup Documentation
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderChannelSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        SMS Channel Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Channel Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            fullWidth
            helperText="A descriptive name for your SMS channel"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_response_enabled}
                onChange={(e) => handleInputChange('auto_response_enabled', e.target.checked)}
              />
            }
            label="Enable Auto-Response"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.greeting_enabled}
                onChange={(e) => handleInputChange('greeting_enabled', e.target.checked)}
              />
            }
            label="Enable Greeting Message"
          />
        </Grid>

        {formData.greeting_enabled && (
          <Grid item xs={12}>
            <TextField
              label="Greeting Message"
              value={formData.greeting_message}
              onChange={(e) => handleInputChange('greeting_message', e.target.value)}
              required
              multiline
              rows={3}
              fullWidth
              helperText="Auto-reply message for incoming SMS (keep it short for SMS)"
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_assignment_enabled}
                onChange={(e) => handleInputChange('auto_assignment_enabled', e.target.checked)}
              />
            }
            label="Auto-assign SMS Conversations"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderVerification = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        SMS Channel Created
      </Typography>
      
      {createdChannel && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {createdChannel.name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Provider:</Typography>
                <Typography>{createdChannel.provider}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Phone Number:</Typography>
                <Typography>{createdChannel.phone_number}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Box display="flex" alignItems="center">
                  {createdChannel.is_verified ? (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  ) : (
                    <Error color="warning" sx={{ mr: 1 }} />
                  )}
                  <Typography>
                    {createdChannel.is_verified ? 'Verified' : 'Not Verified'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">MMS Support:</Typography>
                <Typography>{createdChannel.supports_mms ? 'Yes' : 'No'}</Typography>
              </Grid>
              {createdChannel.display_name && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Display Name:</Typography>
                  <Typography>{createdChannel.display_name}</Typography>
                </Grid>
              )}
              {createdChannel.webhook_url && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Webhook URL:</Typography>
                  <Box display="flex" alignItems="center">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        padding: '4px 8px',
                        borderRadius: 1,
                        flexGrow: 1,
                        mr: 1
                      }}
                    >
                      {createdChannel.webhook_url}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(createdChannel.webhook_url)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Use this URL in your SMS provider webhook configuration
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Button
        variant="contained"
        onClick={handleVerify}
        disabled={loading || !createdChannel}
        startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
        fullWidth
      >
        {loading ? 'Verifying...' : 'Verify SMS Connection'}
      </Button>

      {verificationResult && (
        <Alert 
          severity={verificationResult.verified ? 'success' : 'error'} 
          sx={{ mt: 2 }}
        >
          {verificationResult.verified ? (
            'SMS channel verified successfully! Your phone number is connected and ready to send/receive messages.'
          ) : (
            `Verification failed: ${verificationResult.error}`
          )}
        </Alert>
      )}
    </Box>
  );

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        overflow: 'hidden',
      }}
    >
      <Box display="flex" alignItems="center" mb={2} sx={{ flexShrink: 0 }}>
        <Sms sx={{ mr: 2 }} />
        <Typography variant="h5">
          {editingChannel ? 'Edit' : 'Setup'} SMS Channel
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {index === 0 && renderProviderSelection()}
                {index === 1 && renderSMSConfiguration()}
                {index === 2 && renderChannelSettings()}
                {index === 3 && renderVerification()}
                
                <Box sx={{ mb: 2, mt: 2 }}>
                  <div>
                    {index < 3 && (
                      <Button
                        variant="contained"
                        onClick={index === 2 ? handleSubmit : handleNext}
                        disabled={loading || !validateStep(index)}
                        sx={{ mt: 1, mr: 1 }}
                        startIcon={loading ? <CircularProgress size={20} /> : undefined}
                      >
                        {loading ? 'Processing...' : (index === 2 ? 'Create Channel' : 'Continue')}
                      </Button>
                    )}
                    {index > 0 && index < 3 && (
                      <Button
                        disabled={loading}
                        onClick={handleBack}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </Button>
                    )}
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Paper>
  );
};

export default SMSChannelConfig;